import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Badge,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import { usePorts, type Port } from './useData';
import {
  ReferenceSidebarCollapseButton,
  ReferenceSidebarSplitter,
} from '../reference-workspaces/ReferenceSidebarChrome';
import { useResizablePanel } from '../reference-workspaces/useResizablePanel';

type Country = { id: number; name: string; code?: string | null; isActive?: boolean };

const List: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const confirm = useConfirm();
  const { can } = useAuth();
  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'ports';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const {
    splitRef,
    panelWidth,
    collapsed,
    setCollapsed,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  } = useResizablePanel({
    storageKey: 'ref-ports-split',
    defaultWidth: 320,
    minWidth: 220,
    maxWidth: 480,
  });

  const countryIdFromUrl = searchParams.get('countryId');
  const initialCountryId =
    countryIdFromUrl && Number.isFinite(Number(countryIdFromUrl)) ? Number(countryIdFromUrl) : null;

  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(initialCountryId);
  const [countryFilter, setCountryFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) => (key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc'),
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data: countriesRaw, isLoading: countriesLoading } = useSWR<Country[] | { data: Country[] }>(
    '/countries?page=1&limit=2000',
    fetcher,
  );

  const countries = useMemo(
    () =>
      (Array.isArray(countriesRaw) ? countriesRaw : countriesRaw?.data ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countriesRaw],
  );

  const filteredCountries = useMemo(() => {
    const q = countryFilter.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.code?.trim().toLowerCase().includes(q) ?? false),
    );
  }, [countries, countryFilter]);

  useEffect(() => {
    if (countries.length === 0) {
      if (selectedCountryId != null) setSelectedCountryId(null);
      return;
    }
    const valid =
      selectedCountryId != null && countries.some((c) => c.id === selectedCountryId);
    if (!valid) {
      setSelectedCountryId(countries[0].id);
    }
  }, [countries, selectedCountryId]);

  const activeCountryId = useMemo(() => {
    if (selectedCountryId != null && countries.some((c) => c.id === selectedCountryId)) {
      return selectedCountryId;
    }
    return countries[0]?.id ?? null;
  }, [countries, selectedCountryId]);

  const selectedCountry = useMemo(
    () => (activeCountryId != null ? countries.find((c) => c.id === activeCountryId) ?? null : null),
    [countries, activeCountryId],
  );

  const portsEnabled = activeCountryId != null;

  const { data, total, isLoading, mutate } = usePorts(
    portsEnabled ? search || undefined : undefined,
    page,
    itemsPerPage,
    portsEnabled
      ? {
          countryId: activeCountryId,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }
      : null,
  );

  useEffect(() => {
    setPage(1);
  }, [search, activeCountryId, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    const current = searchParams.get('countryId');
    const nextValue = activeCountryId != null ? String(activeCountryId) : null;
    if (current === nextValue || (current == null && nextValue == null)) return;
    const next = new URLSearchParams(searchParams);
    if (nextValue != null) {
      next.set('countryId', nextValue);
    } else {
      next.delete('countryId');
    }
    setSearchParams(next, { replace: true });
  }, [activeCountryId, searchParams, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const formatDate = moduleConfig.utils.formatDate;

  const newPortHref =
    activeCountryId != null
      ? `${moduleConfig.module.route.new}?countryId=${activeCountryId}`
      : moduleConfig.module.route.new;

  const handleDelete = async (e: React.MouseEvent, row: Port) => {
    e.stopPropagation();
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete “${row.name}”?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await moduleConfig.delete.handler(row.id);
      await mutate();
      showSuccess('Port deleted.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? moduleConfig.delete.errorMessage;
      showError(message);
    }
  };

  const clearCountryFilter = () => setCountryFilter('');
  const clearPortSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  return (
    <div
      ref={splitRef}
      className={cn('flex h-full min-h-0 gap-0 p-1', panelResizing && 'select-none')}
    >
      {collapsed ? (
        <aside
          className={cn(
            'flex w-11 shrink-0 flex-col items-center rounded-2xl border py-2',
            colors.border,
            colors.surface,
          )}
        >
          <ReferenceSidebarCollapseButton collapsed onToggle={() => setCollapsed(false)} />
          <span
            className={cn(
              'mt-2 text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180',
              colors.textMuted,
            )}
          >
            Countries
          </span>
        </aside>
      ) : (
        <div className="shrink-0 min-h-0" style={{ width: panelWidth }}>
          <GeneralList
            className="h-full"
            title={
              <span className="inline-flex items-center gap-1.5">
                Countries
                <ReferenceSidebarCollapseButton collapsed={false} onToggle={() => setCollapsed(true)} />
              </span>
            }
            description={`${countries.length} total`}
            density="compact"
            search={{
              value: countryFilter,
              onChange: setCountryFilter,
              placeholder: 'Filter countries…',
            }}
            hasFilters={Boolean(countryFilter.trim())}
            onClearFilters={clearCountryFilter}
            isLoading={countriesLoading}
            loadingLabel="Loading countries…"
            isEmpty={!countriesLoading && filteredCountries.length === 0}
            emptyState={{
              icon: <DocumentTextIcon className="h-10 w-10" />,
              title: countryFilter.trim() ? 'No countries match' : 'No countries',
              hint: countryFilter.trim()
                ? 'Try a different filter.'
                : 'Create a country first.',
              action: countryFilter.trim() ? (
                <button
                  type="button"
                  onClick={clearCountryFilter}
                  className={cn(
                    'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                    colors.border,
                    colors.text,
                  )}
                >
                  Clear filter
                </button>
              ) : undefined,
            }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                  <th className={cn('sticky top-0 z-10 font-medium', colors.surface)}>Country</th>
                  <th className={cn('sticky top-0 z-10 w-16 font-medium', colors.surface)}>Code</th>
                </tr>
              </thead>
              <tbody>
                {filteredCountries.map((country) => {
                  const active = activeCountryId === country.id;
                  return (
                    <tr
                      key={country.id}
                      onClick={() => setSelectedCountryId(country.id)}
                      className={cn(
                        'cursor-pointer border-b transition-colors last:border-b-0',
                        colors.border,
                        active
                          ? cn(colors.primary, colors.primaryText)
                          : cn(colors.text, colors.interactiveHover),
                      )}
                    >
                      <td className="font-medium">{country.name}</td>
                      <td className={cn('tabular-nums', active ? 'text-white/80' : colors.textMuted)}>
                        {country.code || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GeneralList>
        </div>
      )}

      {!collapsed && (
        <ReferenceSidebarSplitter
          panelWidth={panelWidth}
          minWidth={minWidth}
          maxWidth={maxWidth}
          resizing={panelResizing}
          onMouseDown={startResize}
          label="Resize countries panel"
        />
      )}

      <div className="relative min-h-0 min-w-0 flex-1">
        {collapsed && (
          <div className="absolute left-2 top-2 z-10">
            <ReferenceSidebarCollapseButton collapsed onToggle={() => setCollapsed(false)} />
          </div>
        )}
        <GeneralList
          className="h-full"
          title={selectedCountry?.name ?? 'Ports'}
          description={
            selectedCountry
              ? `${isLoading ? '…' : total ?? 0} port${total === 1 ? '' : 's'}${
                  selectedCountry.code ? ` · ${selectedCountry.code}` : ''
                }`
              : 'Select a country'
          }
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: moduleConfig.search.placeholder,
          }}
          actions={
            canEdit && portsEnabled ? (
              <Link
                to={newPortHref}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                  colors.primary,
                )}
              >
                <PlusIcon className="h-4 w-4" />
                New port
              </Link>
            ) : null
          }
          hasFilters={Boolean(search.trim())}
          onClearFilters={clearPortSearch}
          sorting={{ sortConfig, onSort: handleSort }}
          isLoading={portsEnabled && isLoading}
          loadingLabel="Loading ports…"
          isEmpty={!isLoading && (!portsEnabled || data.length === 0)}
          emptyState={{
            icon: <DocumentTextIcon className="h-12 w-12" />,
            title: !portsEnabled
              ? 'Select a country'
              : search.trim()
                ? moduleConfig.emptyStates.noSearchResults
                : 'No ports yet',
            hint: !portsEnabled
              ? 'Choose a country on the left to view its ports.'
              : search.trim()
                ? 'Try adjusting the search.'
                : moduleConfig.emptyStates.noData,
            action: !portsEnabled ? undefined : search.trim() ? (
              <button
                type="button"
                onClick={clearPortSearch}
                className={cn(
                  'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                Clear search
              </button>
            ) : canEdit ? (
              <Link
                to={newPortHref}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                  colors.primary,
                )}
              >
                <PlusIcon className="h-4 w-4" />
                New port
              </Link>
            ) : undefined,
          }}
          pagination={
            portsEnabled
              ? {
                  page,
                  totalPages,
                  total: total ?? 0,
                  itemsPerPage,
                  pageSizeOptions,
                  onPageChange: setPage,
                  onItemsPerPageChange: (size) => {
                    setItemsPerPage(size);
                    setPage(1);
                  },
                }
              : undefined
          }
        >
          <table className="w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
                <GeneralListSortableTh sortKey="name">Name</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="code">Code</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="isActive">Active</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="createdAt">Created</GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(moduleConfig.module.route.detail(row.id))}
                  className={cn(
                    'cursor-pointer border-b transition-colors last:border-b-0',
                    colors.border,
                    colors.text,
                    colors.interactiveHover,
                  )}
                >
                  <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5">
                      <Link
                        to={moduleConfig.module.route.detail(row.id)}
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="View"
                        aria-label="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                      {canEdit ? (
                        <Link
                          to={moduleConfig.module.route.edit(row.id)}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            colors.interactiveHover,
                          )}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, row)}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            'hover:text-red-600',
                          )}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="pr-6 font-medium whitespace-nowrap">{row.name}</td>
                  <td className={cn('pr-6 whitespace-nowrap', colors.textSecondary)}>{row.code || ''}</td>
                  <td className="pr-6 whitespace-nowrap">
                    <Badge variant={row.isActive ? 'success' : 'danger'}>
                      {row.isActive ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td className={cn('pr-6 whitespace-nowrap', colors.textSecondary)}>
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="w-full" aria-hidden />
                </tr>
              ))}
            </tbody>
          </table>
        </GeneralList>
      </div>
    </div>
  );
};

export default List;
